import { useEffect, useState } from 'react'
import {
  Modal,
  TextInput,
  Textarea,
  Select,
  MultiSelect,
  Switch,
  Group,
  Button,
  Stack,
  Text,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import {
  usePlanoModeloCreate,
  usePlanoModeloUpdate,
} from '../../hooks/usePlanosModelo'
import {
  CATEGORIAS_PLANO,
  ORIGENS_PLANO,
  TIPOS_EQUIPAMENTO,
  TIPOS_PLANO,
} from './constants'
import { extractApiError } from '../../lib/extractApiError'
import type { PlanoModelo, TipoPlano } from '../../types/pmocPlanos'

interface Props {
  open: boolean
  onClose: () => void
  /** Quando informado, modal entra em modo edição. */
  plano?: PlanoModelo | null
}

interface FormState {
  nome: string
  descricao: string
  tipo_plano: TipoPlano
  categoria: string
  origem: string
  ativo: boolean
  referencia_normativa: string
  tipos_equipamento: string[]
}

const INICIAL: FormState = {
  nome: '',
  descricao: '',
  tipo_plano: 'equipamento',
  categoria: 'preventiva',
  origem: 'personalizado',
  ativo: true,
  referencia_normativa: 'ABNT NBR 13971:2014 e Portaria GM/MS 3.523/1998',
  tipos_equipamento: [],
}

export default function PlanoModeloFormModal({ open, onClose, plano }: Props) {
  const editing = !!plano
  const create = usePlanoModeloCreate()
  const update = usePlanoModeloUpdate(plano?.id ?? '')
  const [form, setForm] = useState<FormState>(INICIAL)

  useEffect(() => {
    if (!open) return
    if (plano) {
      setForm({
        nome: plano.nome,
        descricao: plano.descricao,
        tipo_plano: plano.tipo_plano,
        categoria: plano.categoria,
        origem: plano.origem,
        ativo: plano.ativo,
        referencia_normativa: plano.referencia_normativa,
        tipos_equipamento: plano.tipos_equipamento,
      })
    } else {
      setForm(INICIAL)
    }
  }, [open, plano])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!form.nome.trim()) {
      notifications.show({ color: 'red', message: 'Nome é obrigatório.' })
      return
    }
    const payload = {
      ...form,
      tipos_equipamento:
        form.tipo_plano === 'equipamento' ? form.tipos_equipamento : [],
    }
    try {
      if (editing && plano) {
        await update.mutateAsync(payload)
        notifications.show({ color: 'green', message: 'Plano atualizado.' })
      } else {
        await create.mutateAsync(payload)
        notifications.show({ color: 'green', message: 'Plano criado.' })
      }
      onClose()
    } catch (err) {
      notifications.show({
        color: 'red',
        title: 'Falha ao salvar',
        message: extractApiError(err, 'Erro ao salvar plano.'),
      })
    }
  }

  const busy = create.isPending || update.isPending
  const tiposEquipDisabled = form.tipo_plano !== 'equipamento'

  return (
    <Modal
      opened={open}
      onClose={onClose}
      title={editing ? `Editar plano — ${plano?.nome}` : 'Novo plano modelo'}
      size="lg"
      centered
    >
      <form onSubmit={handleSubmit}>
        <Stack gap="sm">
          <Text size="xs" c="dimmed">
            Plano Modelo é uma biblioteca reutilizável. Ao aplicar a um PMOC, gera
            um snapshot imutável (PlanoPMOC) — alterações no modelo não afetam
            PMOCs já aplicados.
          </Text>

          <TextInput
            label="Nome"
            required
            value={form.nome}
            onChange={(e) => setForm({ ...form, nome: e.currentTarget.value })}
            data-testid="plano-modelo-nome"
          />

          <Textarea
            label="Descrição"
            autosize
            minRows={2}
            value={form.descricao}
            onChange={(e) => setForm({ ...form, descricao: e.currentTarget.value })}
          />

          <Group grow>
            <Select
              label="Tipo de plano"
              required
              data={TIPOS_PLANO.map((t) => ({ value: t.value, label: t.label }))}
              value={form.tipo_plano}
              onChange={(v) =>
                v && setForm({ ...form, tipo_plano: v as TipoPlano })
              }
              data-testid="plano-modelo-tipo"
            />
            <Select
              label="Categoria"
              required
              data={CATEGORIAS_PLANO}
              value={form.categoria}
              onChange={(v) => v && setForm({ ...form, categoria: v })}
            />
          </Group>

          <Group grow>
            <Select
              label="Origem"
              data={ORIGENS_PLANO}
              value={form.origem}
              onChange={(v) => v && setForm({ ...form, origem: v })}
            />
            <TextInput
              label="Referência normativa"
              value={form.referencia_normativa}
              onChange={(e) =>
                setForm({ ...form, referencia_normativa: e.currentTarget.value })
              }
            />
          </Group>

          <MultiSelect
            label="Tipos de equipamento compatíveis"
            description={
              tiposEquipDisabled
                ? 'Aplicável só para planos do tipo "Equipamento".'
                : 'Vincule os tipos que este plano cobre. Usado para sugerir planos compatíveis na tela de Equipamento.'
            }
            data={TIPOS_EQUIPAMENTO}
            value={form.tipos_equipamento}
            disabled={tiposEquipDisabled}
            onChange={(v) => setForm({ ...form, tipos_equipamento: v })}
            searchable
            clearable
          />

          <Switch
            label="Ativo"
            description="Planos inativos não aparecem na seleção padrão."
            checked={form.ativo}
            onChange={(e) => setForm({ ...form, ativo: e.currentTarget.checked })}
          />

          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={onClose} disabled={busy}>
              Cancelar
            </Button>
            <Button
              type="submit"
              loading={busy}
              data-testid="plano-modelo-submit"
            >
              {editing ? 'Salvar' : 'Criar plano'}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  )
}
